import { useNavigate } from "react-router";
import { useAppDispatch } from "store/hooks";

import useMediaQuery from "@mui/material/useMediaQuery";

import { MenuItem } from "components/ActionMenu/types";
import { setNotification } from "store/features/notificationSlice";
import {
  IbanInvoice,
  setDuplicateInvoice,
  setSelectedInvoice,
  setShowDeleteModal,
  setShowEditModal,
  setShowQRCodeModal,
} from "store/features/ibanInvoiceSlice";

import { ReactComponent as QrIcon } from "assets/images/actionMenu/qr-menu-icon.svg";
import { ReactComponent as EditIcon } from "assets/images/actionMenu/edit-menu-icon.svg";
import { ReactComponent as CopyIcon } from "assets/images/actionMenu/copy-menu-icon.svg";
import { ReactComponent as DeleteIcon } from "assets/images/actionMenu/delete-menu-icon.svg";
import { ReactComponent as DuplicateIcon } from "assets/images/actionMenu/duplicate-menu-icon.svg";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { routes } from "routes/routes";

interface UseInvoiceActionsProps {
  invoice: IbanInvoice | null;
}

interface UseInvoiceActions {
  actions: MenuItem<IbanInvoice>[];
  ibanInvoiceUrl?: string;
  onCopy: () => Promise<void>;
  onOpenQRCode: () => void;
}

export const useInvoiceActions = (
  props: UseInvoiceActionsProps,
): UseInvoiceActions => {
  const { invoice } = props;
  const { ibanInvoiceUrl } = invoice || {};

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isMd = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const onCopy = async () => {
    if (!ibanInvoiceUrl) return;

    analytics().sendConversionEvent(
      AnalyticsEvents.invoice_details_copy_url,
      null,
      true,
    );

    try {
      await navigator.clipboard.writeText(ibanInvoiceUrl);
      dispatch(
        setNotification({
          severity: "success",
          message: "Посилання скопійоване",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          severity: "error",
          message: "Помилка копіювання",
        }),
      );
    }
  };

  const onDuplicate = (invoice?: IbanInvoice) => {
    if (!invoice) return;

    analytics().sendConversionEvent(
      AnalyticsEvents.invoice_details_duplicate,
      null,
      true,
    );

    dispatch(setDuplicateInvoice(invoice));
    navigate(routes.createInvoice);
  };

  const onOpenQRCode = () => {
    if (!invoice) return;

    analytics().sendConversionEvent(
      AnalyticsEvents.invoice_details_qrcode,
      null,
      true,
    );

    dispatch(setSelectedInvoice(invoice));
    dispatch(setShowQRCodeModal(true));
  };

  const onEdit = () => {
    if (!invoice) return;

    analytics().sendConversionEvent(
      AnalyticsEvents.invoice_details_edit,
      null,
      true,
    );

    dispatch(setSelectedInvoice(invoice));
    dispatch(setShowEditModal(true));
  };

  const onDelete = () => {
    if (!invoice) return;

    analytics().sendConversionEvent(
      AnalyticsEvents.invoice_details_delete,
      null,
      true,
    );

    dispatch(setSelectedInvoice(invoice));
    dispatch(setShowDeleteModal(true));
  };

  const actions: MenuItem<IbanInvoice>[] = [
    {
      id: "copy",
      name: "Скопіювати посилання",
      icon: CopyIcon,
      action: onCopy,
      bottomDivider: true,
      hidden: () => !isMd,
    },
    {
      id: "qr-code",
      name: "QR-код",
      icon: QrIcon,
      action: onOpenQRCode,
      hidden: () => !isMd,
    },
    {
      id: "dublicate",
      name: "Дублювати",
      icon: DuplicateIcon,
      action: onDuplicate,
    },
    {
      id: "edit",
      name: "Редагувати",
      icon: EditIcon,
      action: onEdit,
    },
    {
      id: "delete",
      name: "Видалити",
      icon: DeleteIcon,
      action: onDelete,
    },
  ];

  return {
    actions,
    ibanInvoiceUrl,
    onCopy,
    onOpenQRCode,
  };
};
